import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import './ClaimDiagram.css';

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: false,
    htmlLabels: true,
    curve: 'basis', // Curved lines
    rankSpacing: 60,
    nodeSpacing: 40,
    padding: 20
  }
});

const ClaimDiagram = ({ claim, mode = 'auto' }) => {
  const containerRef = useRef(null);
  const [userMode, setUserMode] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);

  // Determine visualization mode
  const visualMode = userMode || (mode === 'auto' 
    ? (claim?.claim_kind === 'system' ? 'system' : 'method') 
    : mode);

  useEffect(() => {
    setUserMode(null);
  }, [claim]);

  useEffect(() => {
    if (!claim || !claim.features) return;

    const generateMermaidGraph = async () => {
      try {
        const direction = visualMode === 'system' ? 'TB' : 'LR';
        let graphDefinition = `graph ${direction}\n`;
        
        // Add styling classes
        graphDefinition += `  classDef default fill:#fff,stroke:#333,stroke-width:2px,color:#333;\n`;
        graphDefinition += `  classDef system fill:#fff,stroke:#000,stroke-width:2px,rx:0,ry:0;\n`; // Sharp corners
        graphDefinition += `  classDef method fill:#e3f2fd,stroke:#0056b3,stroke-width:2px,rx:5,ry:5;\n`; // Rounded
        graphDefinition += `  classDef data fill:#f3e5f5,stroke:#7b1fa2,stroke-width:1px,stroke-dasharray: 5 5;\n`;
        graphDefinition += `  classDef group fill:none,stroke:#666,stroke-width:2px,stroke-dasharray: 5 5;\n`;

        // 1. Build Hierarchy
        const parentMap = new Map();
        const childrenMap = new Map(); // Parent -> Set<Child>
        const featureMap = new Map();

        claim.features.forEach(f => featureMap.set(f.feature_id, f));

        // Identify containment
        claim.features.forEach(feature => {
          feature.characteristics?.forEach(char => {
            if (char.kind === 'relationship' && char.related_features) {
              const label = (char.relationship || '').toLowerCase();
              if (label.includes('comprise') || label.includes('include') || label.includes('contain') || label.includes('has')) {
                char.related_features.forEach(childId => {
                  if (childId !== feature.feature_id && featureMap.has(childId)) {
                    parentMap.set(childId, feature.feature_id);
                    if (!childrenMap.has(feature.feature_id)) {
                      childrenMap.set(feature.feature_id, new Set());
                    }
                    childrenMap.get(feature.feature_id).add(childId);
                  }
                });
              }
            }
          });
        });

        // Helper to sanitize IDs and Labels
        const cleanId = (id) => id.replace(/[^a-zA-Z0-9]/g, '_');
        const cleanLabel = (label) => label.replace(/"/g, "'").replace(/</g, '&lt;').replace(/>/g, '&gt;');
        
        // Helper to format label with HTML
        const formatLabel = (feature) => {
          const shortLabel = feature.label.length > 40 ? feature.label.substring(0, 37) + '...' : feature.label;
          const idTag = feature.feature ? `<b>${feature.feature}</b>` : '';
          // Use HTML break for multiline
          return `"${idTag}<br/>${cleanLabel(shortLabel)}"`;
        };

        // Recursive function to generate nodes/subgraphs
        const processedNodes = new Set();

        const renderNode = (featureId) => {
          if (processedNodes.has(featureId)) return '';
          processedNodes.add(featureId);

          const feature = featureMap.get(featureId);
          if (!feature) return '';

          const cId = cleanId(featureId);
          const label = formatLabel(feature);
          
          // Determine class
          let className = 'default';
          if (feature.type === 'system_component' || visualMode === 'system') className = 'system';
          if (feature.type === 'data_entity') className = 'data';
          if (visualMode === 'method') className = 'method';

          // Check if it has children (is a subgraph)
          if (childrenMap.has(featureId)) {
            let subgraph = `\n  subgraph ${cId} [${label}]\n`;
            subgraph += `    direction ${direction}\n`; // Inherit direction
            
            // Render children
            childrenMap.get(featureId).forEach(childId => {
              subgraph += renderNode(childId);
            });

            subgraph += `  end\n`;
            // Apply class to the subgraph node itself? Mermaid subgraphs are styled differently.
            // We can style the subgraph border.
            // Mermaid doesn't easily allow classing the subgraph box itself like a node, 
            // but we can style it using style command.
            // subgraph += `  style ${cId} fill:rgba(255,255,255,0.5),stroke:#333,stroke-width:2px\n`;
            return subgraph;
          } else {
            // Leaf node
            return `  ${cId}(${label}):::${className}\n`;
          }
        };

        // Render roots (nodes without parents)
        claim.features.forEach(feature => {
          if (!parentMap.has(feature.feature_id)) {
            graphDefinition += renderNode(feature.feature_id);
          }
        });

        // 2. Create Edges (Non-containment)
        claim.features.forEach(feature => {
          feature.characteristics?.forEach(char => {
            if (char.kind === 'relationship' && char.related_features) {
              const label = (char.relationship || '').toLowerCase();
              // Skip containment
              if (label.includes('comprise') || label.includes('include') || label.includes('contain') || label.includes('has')) {
                return;
              }

              char.related_features.forEach(targetId => {
                if (featureMap.has(targetId)) {
                  const sourceId = cleanId(feature.feature_id);
                  const targetIdClean = cleanId(targetId);
                  const edgeLabel = char.relationship ? `|${cleanLabel(char.relationship)}|` : '';
                  
                  // Mermaid edge
                  graphDefinition += `  ${sourceId} -->${edgeLabel} ${targetIdClean}\n`;
                }
              });
            }
          });
        });

        // Render
        const { svg } = await mermaid.render(`mermaid-${Date.now()}`, graphDefinition);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error("Mermaid generation failed:", err);
        setError("Failed to generate diagram. " + err.message);
      }
    };

    generateMermaidGraph();
  }, [claim, visualMode]);

  return (
    <div className="claim-diagram-container">
      <div className="claim-diagram-controls">
        <button 
          className={`diagram-btn ${visualMode === 'method' ? 'active' : ''}`}
          onClick={() => setUserMode('method')}
        >
          Flowchart
        </button>
        <button 
          className={`diagram-btn ${visualMode === 'system' ? 'active' : ''}`}
          onClick={() => setUserMode('system')}
        >
          Component
        </button>
      </div>

      <div 
        ref={containerRef}
        className="mermaid-container"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
      
      {error && <div className="diagram-error">{error}</div>}
    </div>
  );
};

export default ClaimDiagram;
